import * as React from 'react';
import { useState } from 'react';
import { Box, SimpleGrid } from '@chakra-ui/react';
import {
  Accordion,
  Button,
  Card,
  Checkbox,
  FormControl,
  Heading,
  HStack,
  Input,
  Link,
  Modal,
  Pressable,
  Select,
  Switch,
  Text,
  Tooltip,
  VStack,
} from '@ayble-health/dashboard-components';
import { Badge, badgeVariants, type BadgeProps } from 'components/ui/badge';
import { Chip, chipVariants } from 'components/ui/chip';
import { PressablePill } from 'components/ui/pressable-pill';
import { Section } from './Section';
import { Specimen, State } from './Specimen';
import { ChangeBadge } from './ChangeBadge';
import { ParityBadge, ParityLegend } from './ParityBadge';

const BUTTON_VARIANTS = ['solid', 'outline', 'link', 'plain'] as const;
const BUTTON_SIZES = ['md', 'xs'] as const;

/** Narrows Chakra's ConditionalValue wrapper to plain strings for React keys. */
type VariantOf<T> = T extends string ? T : never;

// Enumerated from the local modules: package variants plus the careOS variants
// inherited from the retired Tag recipe.
const BADGE_VARIANTS = badgeVariants as VariantOf<
  NonNullable<BadgeProps['variant']>
>[];
const CHIP_VARIANTS = chipVariants;

// Button: the variant × size matrix reads best as its own full-width grid,
// so it gets a bespoke layout rather than State cells.
const ButtonSpecimen = () => (
  <Specimen
    name="Button"
    note="variant × size — solid/outline/link/plain, md/xs (recipe consolidated from the legacy secondary/ghost/fullWidth + sm)."
    badge={
      <>
        <ChangeBadge kind="updated" date="Aug 13, 2026" />
        <ParityBadge name="Button" />
      </>
    }
  >
    <VStack gap={4} align="stretch" w="100%">
      {BUTTON_VARIANTS.map((variant) => (
        <HStack key={variant} gap={3} align="center" wrap="wrap">
          <Text minW="80px" fontSize="2xs" color="muted.400" fontFamily="mono">
            {variant}
          </Text>
          {BUTTON_SIZES.map((size) => (
            <Button key={size} variant={variant} size={size}>
              Save
            </Button>
          ))}
        </HStack>
      ))}
    </VStack>
  </Specimen>
);

const TypeSpecimen = () => (
  <Specimen
    name="Text & Heading"
    note="body + headings from dashboard-components"
    badge={<ParityBadge name="Text & Heading" />}
  >
    <State label="Text · default">
      <Text>Body copy — 14px / light 300 / muted.600.</Text>
    </State>
    <State label="Text · emphasized">
      <Text fontSize="md" fontWeight="medium" color="secondary.500">
        Emphasized label
      </Text>
    </State>
    <State label="Heading · md">
      <Heading size="md">Section heading</Heading>
    </State>
    <State label="Heading · sm">
      <Heading size="sm">Smaller heading</Heading>
    </State>
  </Specimen>
);

const StackSpecimen = () => (
  <Specimen
    name="VStack / HStack"
    note="align=stretch, gap=0 by default"
    badge={<ParityBadge name="VStack / HStack" />}
  >
    <State label="VStack">
      <VStack borderWidth="1px" borderColor="cloud">
        <Box bg="feather" p={2}>
          A
        </Box>
        <Box bg="mint" p={2}>
          B
        </Box>
      </VStack>
    </State>
    <State label="HStack gap=3 align=center">
      <HStack
        gap={3}
        align="center"
        borderWidth="1px"
        borderColor="cloud"
        p={2}
      >
        <Box bg="feather" p={2}>
          A
        </Box>
        <Box bg="mint" p={2}>
          B
        </Box>
      </HStack>
    </State>
  </Specimen>
);

const PressableSpecimen = () => (
  <Specimen
    name="Pressable"
    note="native button reset; content-sized"
    badge={<ParityBadge name="Pressable" />}
  >
    <State label="full-width row" wide>
      <Pressable
        w="100%"
        borderWidth="1px"
        borderColor="cloud"
        rounded="md"
        p={2}
        onClick={() => {}}
      >
        <HStack justifyContent="space-between">
          <Text my={0}>Row content</Text>
          <Text my={0} color="seafoam">
            →
          </Text>
        </HStack>
      </Pressable>
    </State>
  </Specimen>
);

const LinkSpecimen = () => (
  <Specimen
    name="Link"
    note="brand-styled anchor"
    badge={<ParityBadge name="Link" />}
  >
    <State label="default">
      <Link href="#colors">Jump to Colors</Link>
    </State>
  </Specimen>
);

const InputSpecimen = () => (
  <Specimen
    name="Input"
    note="single-line text field"
    badge={<ParityBadge name="Input" />}
  >
    <State label="default" wide>
      <Input placeholder="Email" />
    </State>
    <State label="disabled" wide>
      <Input placeholder="Email" disabled />
    </State>
  </Specimen>
);

const SelectSpecimen = () => {
  const [phase, setPhase] = useState('1');
  return (
    <Specimen
      name="Select"
      note="native select wrapper"
      badge={<ParityBadge name="Select" />}
    >
      <State label="default" wide>
        <Select
          selectedValue={phase}
          onValueChange={setPhase}
          placeholder="Select a phase"
        >
          <Select.Item label="Phase 1" value="1" />
          <Select.Item label="Phase 2" value="2" />
          <Select.Item label="Phase 3" value="3" />
        </Select>
      </State>
    </Specimen>
  );
};

const CheckboxSpecimen = () => {
  const [checked, setChecked] = useState(true);
  return (
    <Specimen
      name="Checkbox"
      note="defaults to colorPalette=primary"
      badge={<ParityBadge name="Checkbox" />}
    >
      <State label="unchecked">
        <Checkbox isChecked={false} onChange={() => {}}>
          Notify me
        </Checkbox>
      </State>
      <State label="checked">
        <Checkbox isChecked={checked} onChange={setChecked}>
          Notify me
        </Checkbox>
      </State>
      <State label="disabled">
        <Checkbox isChecked isDisabled onChange={() => {}}>
          Notify me
        </Checkbox>
      </State>
    </Specimen>
  );
};

const SwitchSpecimen = () => {
  const [enabled, setEnabled] = useState(true);
  return (
    <Specimen name="Switch" note="toggle" badge={<ParityBadge name="Switch" />}>
      <State label="on">
        <Switch isChecked={enabled} onChange={setEnabled} />
      </State>
      <State label="off">
        <Switch isChecked={false} onChange={() => {}} />
      </State>
    </Specimen>
  );
};

const FormControlSpecimen = () => (
  <Specimen
    name="FormControl"
    note="label + helper / error, all states together"
    badge={<ParityBadge name="FormControl" />}
  >
    <State label="default" wide>
      <FormControl>
        <FormControl.Label>Email</FormControl.Label>
        <Input placeholder="you@ayblehealth.com" />
        <FormControl.HelperText>
          We never share your email.
        </FormControl.HelperText>
      </FormControl>
    </State>
    <State label="invalid" wide>
      <FormControl invalid>
        <FormControl.Label>Email</FormControl.Label>
        <Input placeholder="you@ayblehealth.com" />
        <FormControl.ErrorMessage>
          Enter a valid email.
        </FormControl.ErrorMessage>
      </FormControl>
    </State>
  </Specimen>
);

const CardSpecimen = () => (
  <Specimen
    name="Card"
    note="surface container"
    badge={<ParityBadge name="Card" />}
  >
    <State label="default" wide>
      <Card p={4} borderColor="cloud" borderWidth={1}>
        <Heading size="sm">Diagnoses</Heading>
        <Text>Type 2 diabetes, hypertension.</Text>
      </Card>
    </State>
  </Specimen>
);

const AccordionSpecimen = () => (
  <Specimen
    name="Accordion"
    note="collapsible sections"
    badge={<ParityBadge name="Accordion" />}
  >
    <State label="first open" wide>
      <Accordion allowMultiple index={[0]}>
        <Accordion.Item index={0}>
          {({ isExpanded }: { isExpanded: boolean }) => (
            <>
              <Accordion.Summary>
                <Text my={0}>Diet</Text>
                <Accordion.Icon />
              </Accordion.Summary>
              <Accordion.Details>
                {isExpanded && <Text my={0}>Diet details go here.</Text>}
              </Accordion.Details>
            </>
          )}
        </Accordion.Item>
        <Accordion.Item index={1}>
          {({ isExpanded }: { isExpanded: boolean }) => (
            <>
              <Accordion.Summary>
                <Text my={0}>Symptoms</Text>
                <Accordion.Icon />
              </Accordion.Summary>
              <Accordion.Details>
                {isExpanded && <Text my={0}>Symptom details go here.</Text>}
              </Accordion.Details>
            </>
          )}
        </Accordion.Item>
      </Accordion>
    </State>
  </Specimen>
);

const ModalSpecimen = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Specimen
      name="Modal"
      note="dialog overlay"
      badge={<ParityBadge name="Modal" />}
    >
      <State label="trigger">
        <Button onClick={() => setIsOpen(true)}>Open modal</Button>
        <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="sm">
          <Modal.Content>
            <Modal.CloseButton />
            <Modal.Header>Edit symptom</Modal.Header>
            <Modal.Body>
              <Text>Modal body content.</Text>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => setIsOpen(false)}>Save</Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal>
      </State>
    </Specimen>
  );
};

const TooltipSpecimen = () => (
  <Specimen
    name="Tooltip"
    note="hover hint"
    badge={<ParityBadge name="Tooltip" />}
  >
    <State label="hover the button">
      <Tooltip label="Click to copy" placement="top">
        <Button variant="outline">Hover me</Button>
      </Tooltip>
    </State>
  </Specimen>
);

const BadgeSpecimen = () => (
  <Specimen
    name="Badge"
    note="positive / negative / outline / neutral from dashboard-components (outline and neutral re-homed from archived Tag — see Archive)."
    badge={<ChangeBadge kind="new" date="Aug 24, 2026" />}
  >
    {BADGE_VARIANTS.map((variant) => (
      <State key={variant} label={variant}>
        <Badge variant={variant}>{variant}</Badge>
      </State>
    ))}
  </Specimen>
);

const ChipSpecimen = () => (
  <Specimen
    name="Chip"
    note="All variants including the six careOS additions that landed upstream in v1.3.0. The package also ships Chip `outline`, which careOS omits in favour of `symptom` — see Archive."
    badge={<ChangeBadge kind="new" date="Aug 24, 2026" />}
  >
    {CHIP_VARIANTS.map((variant) => (
      <State key={variant} label={variant}>
        <Chip variant={variant}>{variant}</Chip>
      </State>
    ))}
  </Specimen>
);

/**
 * Rendered directly after `Chip` on purpose. The two are matched by
 * measurement rather than by shared code — Chip is a slot recipe and cannot be
 * handed to a `button` — so sitting them side by side is what makes a drift
 * between them visible instead of theoretical.
 */
const PressablePillSpecimen = () => {
  const [pressed, setPressed] = useState(true);

  return (
    <Specimen
      name="PressablePill"
      note="The interactive counterpart to Chip: a real <button> with aria-pressed. v1.3.0 adopted the local implementation upstream — Chip and PressablePill now share pill geometry via pillBase in the package."
      badge={
        <>
          <ChangeBadge kind="new" date="Aug 24, 2026" />
          <ParityBadge name="PressablePill" />
        </>
      }
    >
      <State label="pressed">
        <PressablePill pressed={pressed} onClick={() => setPressed((v) => !v)}>
          Today
        </PressablePill>
      </State>
      <State label="unpressed">
        <PressablePill pressed={!pressed} onClick={() => setPressed((v) => !v)}>
          This Week
        </PressablePill>
      </State>
    </Specimen>
  );
};

export const ComponentAudit = () => (
  <Section
    id="atoms"
    title="Components — atoms"
    description="Theme-aware primitive wrappers from @ayble-health/dashboard-components,
      plus local leftovers (Tag). Each card is one component with all its states
      together. (The status badge lives in Patterns → Status, the single
      source of truth.)"
  >
    <VStack gap={4} align="stretch">
      <ParityLegend />

      <ButtonSpecimen />

      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
        <TypeSpecimen />
        <BadgeSpecimen />
        <ChipSpecimen />
        <PressablePillSpecimen />
        <StackSpecimen />
        <InputSpecimen />
        <FormControlSpecimen />
        <CheckboxSpecimen />
        <SwitchSpecimen />
        <SelectSpecimen />
        <LinkSpecimen />
        <PressableSpecimen />
        <CardSpecimen />
        <AccordionSpecimen />
        <ModalSpecimen />
        <TooltipSpecimen />
      </SimpleGrid>
    </VStack>
  </Section>
);
